import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { ActivityLog } from '@/components/ActivityLog';
import DocumentsSection from '@/components/common/DocumentsSection';
import { InvoiceStatusBadge } from '@/components/common/InvoiceStatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AutoGrowTextarea } from '@/components/common/AutoGrowTextarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Receipt, Plus, Search, MoreHorizontal, Send, DollarSign, Pencil, Trash2, CreditCard, Copy, Wrench, FileDown, Mail, Users, ChevronRight, ChevronLeft, Check, FileText, Briefcase, UserCheck, ClipboardList, XCircle, AlertTriangle, MapPin, Tag, ArrowLeft, List, LayoutGrid, ExternalLink } from 'lucide-react';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import PipelineBoard from '@/components/common/PipelineBoard';
import { toast } from 'sonner';
import { Pagination } from '@/components/common/Pagination';
import { useRef } from 'react';
import AttachCustomForm from '@/components/forms/AttachCustomForm';
import AttachedFormsBadge, { useAttachedFormCounts } from '@/components/forms/AttachedFormsBadge';
import { JOB_CATEGORIES, DEFAULT_JOB_CATEGORY, jobCategoryLabel } from '@/constants/jobCategories';
import { EmployeeCombobox } from '@/components/common/EmployeeCombobox';
import { SendDocumentDialog } from '@/components/common/SendDocumentDialog';
import RelatedDocumentsBar from '@/components/common/RelatedDocumentsBar';
import InvoiceDetailView from '@/components/invoices/InvoiceDetailView';
import { fmtMoney, PAYMENT_METHOD_COLORS, PAYMENT_METHOD_LABELS } from '@/components/invoices/paymentMeta';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

const STATUSES = ['all', 'draft', 'sent', 'partial', 'paid', 'overdue', 'in_dispute', 'cancelled'];

// Pipeline ("By Status") columns — canonical invoice statuses (presentation only).
const INVOICE_PIPELINE_COLUMNS = [
  { key: 'draft', label: 'Draft', dotClass: 'bg-gray-400', headerClass: 'bg-gray-50 border-gray-300' },
  { key: 'sent', label: 'Sent', dotClass: 'bg-blue-500', headerClass: 'bg-blue-50 border-blue-300' },
  { key: 'partial', label: 'Partial', dotClass: 'bg-amber-500', headerClass: 'bg-amber-50 border-amber-300' },
  { key: 'paid', label: 'Paid', dotClass: 'bg-emerald-500', headerClass: 'bg-emerald-50 border-emerald-300' },
  { key: 'overdue', label: 'Overdue', dotClass: 'bg-red-500', headerClass: 'bg-red-50 border-red-300' },
  { key: 'in_dispute', label: 'In Dispute', dotClass: 'bg-rose-500', headerClass: 'bg-rose-50 border-rose-300' },
  { key: 'cancelled', label: 'Cancelled', dotClass: 'bg-gray-400', headerClass: 'bg-gray-100 border-gray-300' },
];

export default function InvoicesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [highlightPaymentId, setHighlightPaymentId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  // Attached form counts per invoice (for the "X forms" badge)
  const formCounts = useAttachedFormCounts('invoice', invoices.map(i => i.id));
  const [contacts, setContacts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(location.search).get('status') || 'all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'pipeline'
  const [pipelineInvoices, setPipelineInvoices] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [receiptSelection, setReceiptSelection] = useState('last');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailRelations, setDetailRelations] = useState(null);
  const fetchDetailRelations = (invoiceId) => {
    api.get(`/relations/invoice/${invoiceId}`).then(r => setDetailRelations(r.data)).catch(() => setDetailRelations(null));
  };
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [form, setForm] = useState({ title: '', customer_id: '', job_id: '', project_id: '', payment_method: '', notes: '', due_days: 30, discount_type: 'none', discount_value: 0, items: [{ description: '', name: '', sku: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const [paymentForm, setPaymentForm] = useState({ amount: 0, method: 'cash', reference: '', notes: '', received_date: '' });
  const [cardSurchargeRatePct, setCardSurchargeRatePct] = useState(4);
  
  // Job conversion state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertInvoice, setConvertInvoice] = useState(null);
  const [selectedItemIndices, setSelectedItemIndices] = useState([]);
  const [convertAssignedTo, setConvertAssignedTo] = useState('');
  const [convertJobCategory, setConvertJobCategory] = useState(DEFAULT_JOB_CATEGORY);
  const [convertCategoryTouched, setConvertCategoryTouched] = useState(false);
  const [convertJobTitle, setConvertJobTitle] = useState('');
  const [systemUsers, setSystemUsers] = useState([]);
  const [convertLoading, setConvertLoading] = useState(false);
  
  // Wizard state for new invoices
  const [wizardStep, setWizardStep] = useState(1);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ first_name: '', last_name: '', company_name: '', email: '', phone: '', type: 'customer', account_type: 'residential', is_customer: true });

  // Cancel/Dispute dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelInvoice, setCancelInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInvoice, setDeleteInvoice] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);

  // Payment Request state
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [prInvoice, setPrInvoice] = useState(null);
  const [prForm, setPrForm] = useState({ request_type: 'down_payment', amount: 0, label: '', notes: '' });
  const [disputeInvoice, setDisputeInvoice] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');

  // Custom Forms attachment
  const [customFormsOpen, setCustomFormsOpen] = useState(false);
  const [customFormsInvoice, setCustomFormsInvoice] = useState(null);

  const openCustomForms = (inv) => {
    setCustomFormsInvoice(inv);
    setCustomFormsOpen(true);
  };

  // SKU search state for line items
  const [lineItemResults, setLineItemResults] = useState([]);
  const [activeItemIdx, setActiveItemIdx] = useState(null);
  const searchTimeoutRef = useRef(null);
  const fetchData = useCallback(async () => {
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [invRes, contRes, jobRes, usersRes] = await Promise.all([
        api.get('/invoices', { params }),
        api.get('/contacts', { params: { page_size: 500 } }),
        api.get('/jobs', { params: { page_size: 500 } }),
        api.get('/auth/users').catch(() => ({ data: [] }))
      ]);
      const invData = invRes.data;
      setInvoices(invData.items || []);
      setTotal(invData.total || 0);
      setTotalPages(invData.total_pages || 1);
      const contData = contRes.data;
      setContacts(contData.items || contData);
      const jobData = jobRes.data;
      setJobs(jobData.items || jobData);
      setSystemUsers(usersRes.data);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [statusFilter, search, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/settings/company').then(res => {
      if (res.data?.credit_card_surcharge_rate != null) setCardSurchargeRatePct(res.data.credit_card_surcharge_rate);
    }).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  // Pipeline view loads ALL invoices once (independent of the status tab filter).
  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const params = { page_size: 1000 };
      if (search) params.search = search;
      const res = await api.get('/invoices', { params });
      setPipelineInvoices(res.data.items || []);
    } catch { /* keep previous */ }
    finally { setPipelineLoading(false); }
  }, [search]);

  useEffect(() => {
    if (viewMode === 'pipeline') fetchPipeline();
  }, [viewMode, fetchPipeline]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (location.state?.createFor && contacts.length > 0) {
      const customerId = location.state.createFor;
      const contactName = location.state.contactName || '';
      setEditing(null);
      setForm({ 
        title: contactName ? `${t('invoices.title')} - ${contactName}` : '', 
        customer_id: customerId, 
        job_id: '', 
        project_id: '', 
        payment_method: '', 
        notes: '', 
        due_days: 30, 
        items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }] 
      });
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, contacts, navigate, t]);

  // Handle openInvoiceId from Project Detail / Contact Profile navigation.
  // Fetch the invoice DIRECTLY by id — with pagination (25/page) an invoice
  // outside the current page never matched `.find()` on the loaded list,
  // silently landing the user on the plain list instead (TEMA B1).
  useEffect(() => {
    if (location.state?.openInvoiceId) {
      const invoiceId = location.state.openInvoiceId;
      const paymentId = location.state.highlightPaymentId || null;
      navigate(location.pathname, { replace: true, state: {} });
      api.get(`/invoices/${invoiceId}`).then(res => {
        openEdit(res.data);
        if (paymentId) setHighlightPaymentId(paymentId);
      }).catch(() => {
        toast.error('No se pudo abrir la factura');
      });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle createForProject from Project Detail navigation.
  // Tie the new invoice to the project and skip the customer-selection step.
  useEffect(() => {
    if (location.state?.createForProject) {
      const projectId = location.state.createForProject;
      const customerId = location.state.createFor || '';
      openCreate();
      setTimeout(() => {
        setForm(prev => ({ ...prev, project_id: projectId, customer_id: customerId || prev.customer_id }));
        if (customerId) setWizardStep(2);
      }, 100);
      navigate(location.pathname, { replace: true, state: {} });
      toast.info(t('invoices.creatingForProject', { defaultValue: 'Creating invoice for this project' }));
    }
  }, [location.state, navigate, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle useTemplate from Invoice Templates page navigation
  useEffect(() => {
    if (location.state?.useTemplate) {
      const template = location.state.useTemplate;
      setEditing(null);
      setForm({ 
        title: template.name || '', 
        customer_id: '', 
        job_id: '', 
        project_id: '', 
        payment_method: '', 
        notes: template.notes || '', 
        due_days: template.due_days || 30,
        items: template.items?.length ? template.items.map(i => ({
          description: i.description || i.name || '',
          name: i.name || '',
          sku: i.sku || '',
          quantity: i.quantity || 1,
          unit: i.unit || 'unit',
          unit_price: i.unit_price || 0
        })) : [{ description: '', name: '', sku: '', quantity: 1, unit: 'unit', unit_price: 0 }]
      });
      setWizardStep(1);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
      toast.success(`${t('invoiceTemplates.useTemplate')}: "${template.name}"`);
      
      // Register template usage
      api.post(`/invoice-templates/${template.id}/use`).catch(() => {});
    }
  }, [location.state, navigate, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setIsViewMode(false);
    setForm({ title: '', customer_id: '', job_id: '', project_id: '', payment_method: '', notes: '', due_days: 30, discount_type: 'none', discount_value: 0, items: [{ description: '', name: '', sku: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
    setWizardStep(1);
    setShowNewCustomerForm(false);
    setNewCustomerForm({ first_name: '', last_name: '', company_name: '', email: '', phone: '', type: 'customer', account_type: 'residential', is_customer: true });
    setDialogOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setIsViewMode(true);
    fetchDetailRelations(inv.id);
    setForm({
      title: inv.title, description: inv.description || '', customer_id: inv.customer_id || '', job_id: inv.job_id || '',
      project_id: inv.project_id || '', payment_method: inv.payment_method || '',
      notes: inv.notes || '', due_days: 30,
      discount_type: inv.discount_type || 'none',
      discount_value: inv.discount_value || 0,
      items: inv.items?.length ? inv.items.map(i => ({ description: i.description || '', name: i.name || '', sku: i.sku || '', quantity: i.quantity || 1, unit: i.unit || 'unit', unit_price: i.unit_price || 0 })) : [{ description: '', name: '', sku: '', quantity: 1, unit: 'unit', unit_price: 0 }]
    });
    setWizardStep(2); // Skip to items for editing
    setDialogOpen(true);
  };

  // Create new customer inline
  const handleCreateCustomer = async () => {
    if (!newCustomerForm.first_name.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    try {
      const res = await api.post('/contacts', newCustomerForm);
      setContacts([...contacts, res.data]);
      setForm({ ...form, customer_id: res.data.id });
      setShowNewCustomerForm(false);
      setNewCustomerForm({ first_name: '', last_name: '', company_name: '', email: '', phone: '', type: 'customer', account_type: 'residential', is_customer: true });
      toast.success(t('invoices.wizard.newCustomerCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const handleNextStep = () => {
    if (wizardStep === 1 && !form.customer_id) {
      toast.error(t('invoices.wizard.customerRequired'));
      return;
    }
    setWizardStep(prev => Math.min(prev + 1, 2));
  };

  const handlePrevStep = () => {
    setWizardStep(prev => Math.max(prev - 1, 1));
  };

  const createFromJob = async (jobId) => {
    try {
      const res = await api.post(`/invoices/from-job/${jobId}`);
      toast.success(t('success.created'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', name: '', sku: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };

  // SKU search for invoice line items
  const searchLineItems = async (query, idx) => {
    setActiveItemIdx(idx);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.length < 1) {
      setLineItemResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/products/search', { params: { q: query } });
        setLineItemResults(res.data);
      } catch { setLineItemResults([]); }
    }, 150);
  };

  const selectLineItem = (lineItem, idx) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      sku: lineItem.sku,
      name: lineItem.name,
      description: lineItem.description || lineItem.name,
      quantity: lineItem.default_quantity || 1,
      unit: lineItem.unit || 'unit',
      unit_price: lineItem.unit_price || 0
    };
    setForm({ ...form, items });
    setLineItemResults([]);
    setActiveItemIdx(null);
    toast.success(`Item agregado: ${lineItem.name}`);
  };

  const calcSubtotal = () => form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0);
  const calcDiscountAmount = () => {
    const subtotal = calcSubtotal();
    if (form.discount_type === 'percentage' && form.discount_value > 0) {
      return Math.round(subtotal * (form.discount_value / 100) * 100) / 100;
    } else if (form.discount_type === 'fixed' && form.discount_value > 0) {
      return Math.min(form.discount_value, subtotal);
    }
    return 0;
  };
  const calcSurcharge = () => form.payment_method === 'credit_card' ? (calcSubtotal() - calcDiscountAmount()) * (cardSurchargeRatePct / 100) : 0;
  const calcTotal = () => calcSubtotal() - calcDiscountAmount() + calcSurcharge();

  // --- Job Conversion (categories come from the single source of truth) ---
  const openConvertDialog = (inv) => {
    setConvertInvoice(inv);
    setSelectedItemIndices([]);
    setConvertAssignedTo('');
    setConvertJobCategory(DEFAULT_JOB_CATEGORY);
    setConvertCategoryTouched(false);
    setConvertJobTitle('');
    setConvertDialogOpen(true);
  };

  const toggleItemSelection = (idx) => {
    setSelectedItemIndices(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const isItemConverted = (inv, idx) => {
    return inv?.converted_items && inv.converted_items[String(idx)];
  };

  const getConvertedJobInfo = (inv, idx) => {
    if (inv?.converted_items && inv.converted_items[String(idx)]) {
      return inv.converted_items[String(idx)];
    }
    return null;
  };

  const handleConvertToJob = async () => {
    if (!convertInvoice) return;
    if (selectedItemIndices.length === 0) {
      toast.error('Select at least one item');
      return;
    }
    if (!convertAssignedTo) {
      toast.error('Select an employee');
      return;
    }
    setConvertLoading(true);
    try {
      const res = await api.post(`/invoices/${convertInvoice.id}/convert-to-job`, {
        selected_item_indices: selectedItemIndices,
        assigned_to: convertAssignedTo,
        job_category: convertJobCategory,
        job_title: convertJobTitle
      });
      const { job, remaining_items } = res.data;
      toast.success(`Job ${job.job_number} created successfully`);
      
      // Reset selections but keep dialog open if there are remaining items
      if (remaining_items > 0) {
        setSelectedItemIndices([]);
        setConvertAssignedTo('');
        setConvertCategoryTouched(false);
        setConvertJobTitle('');
        // Refresh invoice data to get updated converted_items
        const updatedInv = await api.get(`/invoices/${convertInvoice.id}`);
        setConvertInvoice(updatedInv.data);
        fetchData();
      } else {
        setConvertDialogOpen(false);
        fetchData();
        toast.success('All items have been assigned to jobs');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error converting');
    } finally {
      setConvertLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Title is auto-generated if empty
    if (!form.customer_id && !editing) { 
      toast.error(t('invoices.wizard.customerRequired')); 
      return; 
    }
    try {
      if (editing) {
        await api.put(`/invoices/${editing.id}`, form);
        toast.success(t('success.updated'));
      } else {
        await api.post('/invoices', form);
        toast.success(t('success.created'));
      }
      // Auto-save retired (Alian's video 2026-02-13).
      setDialogOpen(false);
      setLineItemResults([]);
      setActiveItemIdx(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  // Refresh the invoice currently open in the detail/view dialog so its
  // status bar reflects changes made from the top action buttons.
  const refreshEditingInvoice = async (id) => {
    if (!editing || editing.id !== id) return;
    try {
      const res = await api.get(`/invoices/${id}`);
      setEditing(res.data);
      fetchDetailRelations(id);
    } catch { /* non-fatal */ }
  };

  const [sendDialogInv, setSendDialogInv] = useState(null);
  const openSendDialog = (inv) => setSendDialogInv(inv);
  const handleSendDialogSent = (updated) => {
    setSendDialogInv(null);
    fetchData();
    if (updated?.id && editing?.id === updated.id) {
      setEditing(prev => ({ ...prev, ...updated }));
    }
  };

  const openPaymentDialog = (inv, paymentRequest = null) => {
    setSelectedInvoice(inv);
    let defaultAmount = inv.balance_due || 0;
    let prId = '';
    if (paymentRequest) {
      defaultAmount = paymentRequest.amount || 0;
      prId = paymentRequest.id;
    } else {
      // Alian: "que sea exclusivamente lo que yo le mande a pedir" — si hay
      // una solicitud de pago activa (sent), el default es SU monto, no el
      // balance completo de la factura.
      const activeSent = (inv.payment_requests || []).find(pr => pr.status === 'sent');
      if (activeSent) {
        defaultAmount = activeSent.amount || 0;
        prId = activeSent.id;
      }
    }
    setPaymentForm({
      amount: defaultAmount,
      amountStr: defaultAmount > 0 ? fmtMoney(defaultAmount) : '',
      method: 'cash', reference: '', notes: '', payment_request_id: prId,
      received_date: new Date().toISOString().slice(0, 10),
    });
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    try {
      const { amountStr, ...payload } = paymentForm;
      await api.post(`/invoices/${selectedInvoice.id}/payment`, payload);
      toast.success(t('success.saved'));
      setPaymentDialogOpen(false);
      fetchData();
      await refreshEditingInvoice(selectedInvoice.id);
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  // Fix K (2026-09-02): "Send Receipt" — por defecto el ultimo pago, o "all"
  // para un recibo consolidado con todos los pagos de la factura.
  // Prompt Z: acepta un paymentId para preseleccionar el pago de la fila
  // desde la que se toco "Receipt" en la nueva tabla de Payment History.
  const openReceiptDialog = (inv, paymentId = null) => {
    setReceiptInvoice(inv);
    setReceiptSelection(paymentId || 'last');
    setReceiptDialogOpen(true);
  };

  // Prompt Z: dropdown "Current Status" del bloque INVOICE WORKFLOW.
  // draft/sent se cambian directo; cancelled/in_dispute abren su dialogo
  // (llevan motivo obligatorio); partial/paid los manejan los pagos.
  const handleInvoiceStatusChange = async (key) => {
    if (!editing) return;
    if (key === 'cancelled') { openCancelDialog(editing); return; }
    if (key === 'in_dispute') { openDisputeDialog(editing); return; }
    if (key === editing.status) return;
    try {
      await api.put(`/invoices/${editing.id}`, { status: key });
      toast.success(t('success.updated'));
      fetchData();
      await refreshEditingInvoice(editing.id);
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleSendReceipt = async () => {
    if (!receiptInvoice) return;
    setSendingReceipt(true);
    try {
      const payments = receiptInvoice.payments || [];
      const body = receiptSelection === 'all'
        ? { payment_ids: payments.map(p => p.id) }
        : receiptSelection === 'last' ? {} : { payment_ids: [receiptSelection] };
      const res = await api.post(`/invoices/${receiptInvoice.id}/send-receipt`, body);
      toast.success(`Receipt sent to ${(res.data.to || []).join(', ') || 'customer'}`);
      setReceiptDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSendingReceipt(false);
    }
  };

  // Payment Request handlers
  const openPaymentRequestDialog = (inv) => {
    setPrInvoice(inv);
    const existingPRs = (inv.payment_requests || []).filter(pr => pr.status !== 'cancelled');
    const totalRequested = existingPRs.reduce((sum, pr) => sum + (pr.amount || 0), 0);
    const remaining = Math.max(0, (inv.total || 0) - totalRequested);
    // Fix D (2026-08-31): "esto es una inspeccion... y no hace falta un
    // deposito" — an invoice linked to a Site Inspection job requests the
    // Total Amount Due by default instead of a Down Payment. Every other
    // job type keeps the previous default.
    const linkedJob = inv.job_id ? jobs.find(j => j.id === inv.job_id) : null;
    const defaultRequestType = linkedJob?.type === 'site_inspection' ? 'full_payment' : 'down_payment';
    setPrForm({ request_type: defaultRequestType, amount: Math.round(remaining * 100) / 100, label: '', notes: '', amount_mode: 'fixed', percentage: 0, percentage_base: 'total' });
    setPrDialogOpen(true);
  };

  const handleCreatePaymentRequest = async (e) => {
    e.preventDefault();
    if (!prInvoice) return;
    try {
      await api.post(`/invoices/${prInvoice.id}/payment-request`, prForm);
      toast.success('Payment request created');
      setPrDialogOpen(false);
      fetchData();
      // Refresh the editing invoice if it's open
      if (editing && editing.id === prInvoice.id) {
        const res = await api.get(`/invoices/${prInvoice.id}`);
        setEditing(res.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error creating payment request'); }
  };

  const handleDeletePaymentRequest = async (invoiceId, requestId) => {
    try {
      await api.delete(`/invoices/${invoiceId}/payment-request/${requestId}`);
      toast.success('Payment request cancelled');
      fetchData();
      if (editing && editing.id === invoiceId) {
        const res = await api.get(`/invoices/${invoiceId}`);
        setEditing(res.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openDeleteDialog = (inv) => {
    setDeleteInvoice(inv);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteInvoice) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${deleteInvoice.id}`);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeleteInvoice(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.deletingError'));
    } finally {
      setDeleting(false);
    }
  };

  // ---- Delete (single) ----
  // Cancel invoice with mandatory reason
  const openCancelDialog = (inv) => {
    setCancelInvoice(inv);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelInvoice = async () => {
    if (!cancelInvoice || !cancelReason.trim()) {
      toast.error('Cancellation reason is required');
      return;
    }
    try {
      await api.post(`/invoices/${cancelInvoice.id}/cancel`, { reason: cancelReason.trim() });
      toast.success('Invoice cancelled');
      setCancelDialogOpen(false);
      fetchData();
      await refreshEditingInvoice(cancelInvoice.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cancelar'); }
  };

  // Dispute invoice with mandatory reason
  const openDisputeDialog = (inv) => {
    setDisputeInvoice(inv);
    setDisputeReason('');
    setDisputeDialogOpen(true);
  };

  const handleDisputeInvoice = async () => {
    if (!disputeInvoice || !disputeReason.trim()) {
      toast.error('Dispute reason is required');
      return;
    }
    try {
      await api.post(`/invoices/${disputeInvoice.id}/dispute`, { reason: disputeReason.trim() });
      toast.success('Invoice marked in dispute');
      setDisputeDialogOpen(false);
      fetchData();
      await refreshEditingInvoice(disputeInvoice.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al marcar en disputa'); }
  };

  const copyPublicLink = (inv) => {
    const url = `${window.location.origin}/pay/${inv.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success(t('success.copied'));
  };

  const downloadPDF = async (inv) => {
    try {
      const res = await api.get(`/pdf/invoice/${inv.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factura_${inv.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('common.download'));
    } catch { toast.error(t('errors.generic')); }
  };



  const getCustomerName = (id) => { const c = contacts.find(c => c.id === id); return c ? contactPrimaryLabel(c) : '-'; };
  const getJobNumber = (id) => { const j = jobs.find(j => j.id === id); return j ? j.job_number : '-'; };

  return (
    <div data-testid="invoices-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('invoices.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('invoices.totalInvoices', { count: invoices.length })}</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="create-from-job-btn" variant="outline" size="sm" className="border-[#003366]/20 text-[#003366]">
                <Wrench className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">{t('jobs.title')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-60 overflow-y-auto">
              {jobs.filter(j => !j.invoice_id && j.status !== 'cancelled').map(j => (
                <DropdownMenuItem key={j.id} onClick={() => createFromJob(j.id)}>
                  {j.job_number} - {j.title}
                </DropdownMenuItem>
              ))}
              {jobs.filter(j => !j.invoice_id && j.status !== 'cancelled').length === 0 && (
                <DropdownMenuItem disabled>{t('jobs.noJobs')}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {hasPermission('invoices', 'create') && <Button data-testid="new-invoice-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-1 sm:mr-2" /> {t('invoices.newInvoice')}
          </Button>}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-invoices" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search') + '...'} className="pl-10 h-9 bg-white" />
        </div>
        <div className="tabs-scroll flex-1 min-w-0">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-max">
            <TabsList className="bg-white border border-gray-200 h-9">
              {STATUSES.map(s => (
                <TabsTrigger key={s} value={s} data-testid={`inv-filter-${s}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                  {t(`invoices.statuses.${s}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center rounded-lg border border-gray-200 bg-white h-9 overflow-hidden self-start">
          <button
            type="button" data-testid="invoices-view-list"
            onClick={() => setViewMode('list')}
            className={`px-3 h-full flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-[#003366] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <List className="w-3.5 h-3.5" /> <span className="hidden sm:inline">List</span>
          </button>
          <button
            type="button" data-testid="invoices-view-pipeline"
            onClick={() => setViewMode('pipeline')}
            className={`px-3 h-full flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'pipeline' ? 'bg-[#003366] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> <span className="hidden sm:inline">By Status</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { labelKey: 'invoices.title', value: invoices.reduce((s, i) => s + (i.total || 0), 0), color: 'text-[#003366]' },
          { labelKey: 'statusBadges.paid', value: invoices.reduce((s, i) => s + (i.amount_paid || 0), 0), color: 'text-emerald-600' },
          { labelKey: 'statusBadges.pending', value: invoices.reduce((s, i) => s + (i.balance_due || 0), 0), color: 'text-amber-600' },
          { labelKey: 'invoices.statuses.paid', value: invoices.filter(i => i.status === 'paid').length, color: 'text-green-600', isCurrency: false },
        ].map((card, i) => (
          <Card key={i} className="bg-white border-gray-100">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{t(card.labelKey)}</p>
              <p className={`text-xl font-bold mt-1 ${card.color}`}>
                {card.isCurrency === false ? card.value : `$${card.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {viewMode === 'pipeline' ? (
        pipelineLoading ? (
          <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
        ) : (
          <PipelineBoard
            columns={INVOICE_PIPELINE_COLUMNS}
            items={pipelineInvoices}
            getStatus={(inv) => (inv.effective_status || inv.status || 'draft')}
            sumOf={(inv) => inv.total || 0}
            emptyLabel={t('invoices.noInvoices')}
            renderCard={(inv) => (
              <div
                key={inv.id}
                data-testid={`pipeline-card-${inv.id}`}
                onClick={() => openEdit(inv)}
                className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm hover:shadow-md cursor-pointer transition-shadow"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono font-semibold text-gray-700">{inv.invoice_number}</span>
                  <span className="text-sm font-bold text-gray-900">${(inv.total || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-600 truncate">{getCustomerName(inv.customer_id)}</p>
                {inv.title && <p className="text-[11px] text-gray-400 truncate">{inv.title}</p>}
                <div className="flex items-center justify-between mt-2 text-[11px]">
                  {inv.balance_due > 0 ? (
                    <span className="text-amber-600 font-medium">Due: ${(inv.balance_due || 0).toLocaleString()}</span>
                  ) : (
                    <span className="text-emerald-600 font-medium">Paid</span>
                  )}
                  {inv.due_date && <span className="text-gray-400">{String(inv.due_date).slice(0, 10)}</span>}
                </div>
              </div>
            )}
          />
        )
      ) : (
      <>
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : invoices.length === 0 ? (
            <div className="py-16 text-center">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('invoices.noInvoices')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('invoices.createFirstInvoice')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('invoices.invoiceNumber')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('common.customer')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">Job Site</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.total')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">Balance</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                      <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className={`border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer ${inv.status === 'paid' ? 'bg-gray-50/70 opacity-70' : ''}`} onClick={() => openEdit(inv)}>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-mono text-gray-500">{inv.invoice_number}</p>
                        {inv.job_id && <p className="text-xs text-gray-400">{getJobNumber(inv.job_id)}</p>}
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{inv.title}</p>
                        {inv.source_estimate_id && <p className="text-xs text-blue-500">From Estimate {inv.source_estimate_number || ''}</p>}
                        {inv.converted_items && Object.keys(inv.converted_items).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1" onClick={e => e.stopPropagation()}>
                            {Array.from(
                              new Map(Object.values(inv.converted_items).map((info) => [info.job_id, info])).values()
                            ).map((info) => (
                              <button
                                key={info.job_id}
                                type="button"
                                data-testid={`invoice-job-link-${inv.id}-${info.job_id}`}
                                onClick={() => navigate(`/jobs/${info.job_id}`)}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
                                title={`Open ${info.job_number}`}
                              >
                                <Briefcase className="w-2.5 h-2.5" />{info.job_number}
                                {info.job_type ? ` · ${jobCategoryLabel(info.job_type)}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                        {inv.surcharge > 0 && <p className="text-xs text-amber-600">+{Math.round((inv.surcharge_rate || 0) * 100)}%</p>}
                        {inv.discount_amount > 0 && <p className="text-xs text-red-500">Discount: -${fmtMoney(inv.discount_amount)}</p>}
                        {inv.cancellation_reason && <p className="text-xs text-gray-400 italic truncate max-w-[200px]" title={inv.cancellation_reason}>{t('invoices.reasonLabel')} {inv.cancellation_reason}</p>}
                        {inv.dispute_reason && <p className="text-xs text-rose-500 italic truncate max-w-[200px]" title={inv.dispute_reason}>Dispute: {inv.dispute_reason}</p>}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">{getCustomerName(inv.customer_id)}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        {inv.well_address ? (
                          <span data-testid={`inv-job-site-${inv.id}`} className="text-xs text-gray-600">
                            {inv.well_address}{(inv.well_city || inv.well_state) ? `, ${[inv.well_city, inv.well_state].filter(Boolean).join(', ')}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5"><InvoiceStatusBadge invoice={inv} /></td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700">${(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-5 py-3.5 text-right hidden lg:table-cell">
                        {inv.status === 'partial' && inv.total > 0 ? (
                          <div className="space-y-1" data-testid={`payment-progress-${inv.id}`}>
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="text-xs text-emerald-600 font-medium">${fmtMoney(inv.amount_paid)}</span>
                              <span className="text-xs text-gray-400">/</span>
                              <span className="text-xs text-gray-500">${fmtMoney(inv.total)}</span>
                            </div>
                            <div className="w-full max-w-[120px] ml-auto bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(100, ((inv.amount_paid || 0) / inv.total) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className={inv.balance_due > 0 ? 'text-sm font-semibold text-amber-600' : 'text-sm font-semibold text-emerald-600'}>
                            ${(inv.balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        )}
                        {inv.payments?.length > 0 && (
                          <p className="text-[10px] text-gray-400 uppercase mt-0.5" data-testid={`last-payment-method-${inv.id}`}>
                            via {inv.payments[inv.payments.length - 1].method}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`invoice-actions-${inv.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={`send-invoice-${inv.id}`} onClick={() => openSendDialog(inv)}>
                              <Send className="w-3.5 h-3.5 mr-2" />{inv.status === 'draft' ? t('invoices.send') : t('invoices.resend', { defaultValue: 'Resend' })}
                            </DropdownMenuItem>
                            {['sent', 'partial', 'in_dispute'].includes(inv.status) && (
                              <DropdownMenuItem data-testid={`record-payment-${inv.id}`} onClick={() => openPaymentDialog(inv)}>
                                <DollarSign className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('invoices.collectPayment')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem data-testid={`convert-to-job-${inv.id}`} onClick={() => openConvertDialog(inv)}>
                              <Briefcase className="w-3.5 h-3.5 mr-2 text-violet-500" />Convert to Job
                            </DropdownMenuItem>
                            {!['paid', 'cancelled'].includes(inv.status) && inv.balance_due > 0 && (
                              <DropdownMenuItem data-testid={`payment-request-${inv.id}`} onClick={() => openPaymentRequestDialog(inv)}>
                                <CreditCard className="w-3.5 h-3.5 mr-2 text-blue-500" />Payment Request
                              </DropdownMenuItem>
                            )}
                            {!['paid', 'cancelled'].includes(inv.status) && hasPermission('invoices', 'edit') && <DropdownMenuItem onClick={() => openEdit(inv)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => copyPublicLink(inv)}><Copy className="w-3.5 h-3.5 mr-2" />{t('invoices.paymentLink')}</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`download-invoice-pdf-${inv.id}`} onClick={() => downloadPDF(inv)}><FileDown className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('invoices.downloadPdf')}</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`custom-forms-${inv.id}`} onClick={() => openCustomForms(inv)}>
                              <FileText className="w-3.5 h-3.5 mr-2 text-emerald-500" />Custom Forms
                            </DropdownMenuItem>
                            {!['paid', 'cancelled', 'in_dispute'].includes(inv.status) && (
                              <DropdownMenuItem data-testid={`dispute-invoice-${inv.id}`} onClick={() => openDisputeDialog(inv)} className="text-rose-600">
                                <AlertTriangle className="w-3.5 h-3.5 mr-2" />In Dispute
                              </DropdownMenuItem>
                            )}
                            {!['paid', 'cancelled'].includes(inv.status) && hasPermission('invoices', 'cancel') && (
                              <DropdownMenuItem data-testid={`cancel-invoice-${inv.id}`} onClick={() => openCancelDialog(inv)} className="text-red-600">
                                <XCircle className="w-3.5 h-3.5 mr-2" />Cancel Invoice
                              </DropdownMenuItem>
                            )}
                            {hasPermission('invoices', 'delete') && (
                              <DropdownMenuItem data-testid={`delete-invoice-${inv.id}`} onClick={() => openDeleteDialog(inv)} className="text-red-700">
                                <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : invoices.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Receipt className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('invoices.noInvoices')}</p>
          </CardContent></Card>
        ) : (
          invoices.map((inv, idx) => (
            <Card key={inv.id} data-testid={`invoice-mobile-${inv.id}`} className={`bg-white border-gray-100 shadow-sm mobile-card-enter ${inv.status === 'paid' ? 'opacity-70' : ''}`} style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{inv.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-gray-400 font-mono">{inv.invoice_number}</p>
                      {inv.surcharge > 0 && <span className="text-[10px] text-amber-600">+{Math.round((inv.surcharge_rate || 0) * 100)}%</span>}
                    </div>
                    {inv.cancellation_reason && <p className="text-[10px] text-gray-400 italic truncate">{t('invoices.reasonLabel')} {inv.cancellation_reason}</p>}
                    {inv.dispute_reason && <p className="text-[10px] text-rose-500 italic truncate">Dispute: {inv.dispute_reason}</p>}
                  </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem data-testid={`send-invoice-mobile-${inv.id}`} onClick={() => openSendDialog(inv)}>
                        <Send className="w-3.5 h-3.5 mr-2" />{inv.status === 'draft' ? t('invoices.send') : t('invoices.resend', { defaultValue: 'Resend' })}
                      </DropdownMenuItem>
                      {['sent', 'partial', 'in_dispute'].includes(inv.status) && <DropdownMenuItem onClick={() => openPaymentDialog(inv)}><DollarSign className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('invoices.collectPayment')}</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => openConvertDialog(inv)}><Briefcase className="w-3.5 h-3.5 mr-2 text-violet-500" />Convert to Job</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPDF(inv)}><FileDown className="w-3.5 h-3.5 mr-2 text-blue-500" />PDF</DropdownMenuItem>
                      {!['paid', 'cancelled', 'in_dispute'].includes(inv.status) && (
                        <DropdownMenuItem onClick={() => openDisputeDialog(inv)} className="text-rose-600"><AlertTriangle className="w-3.5 h-3.5 mr-2" />In Dispute</DropdownMenuItem>
                      )}
                      {!['paid', 'cancelled'].includes(inv.status) && hasPermission('invoices', 'cancel') && (
                        <DropdownMenuItem onClick={() => openCancelDialog(inv)} className="text-red-600"><XCircle className="w-3.5 h-3.5 mr-2" />Cancel Invoice</DropdownMenuItem>
                      )}
                      {hasPermission('invoices', 'delete') && (
                        <DropdownMenuItem data-testid={`delete-invoice-mobile-${inv.id}`} onClick={() => openDeleteDialog(inv)} className="text-red-700"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <InvoiceStatusBadge invoice={inv} />
                    <AttachedFormsBadge
                      linkedType="invoice"
                      linkedId={inv.id}
                      count={formCounts[inv.id] ?? null}
                      onClick={() => openCustomForms(inv)}
                    />
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-gray-800">${(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    {inv.status === 'partial' && inv.total > 0 ? (
                      <div className="mt-1" data-testid={`payment-progress-mobile-${inv.id}`}>
                        <p className="text-[10px] text-emerald-600">${fmtMoney(inv.amount_paid)} / ${fmtMoney(inv.total)}</p>
                        <div className="w-20 bg-gray-200 rounded-full h-1 mt-0.5">
                          <div className="bg-emerald-500 h-1 rounded-full" style={{ width: `${Math.min(100, ((inv.amount_paid || 0) / inv.total) * 100)}%` }} />
                        </div>
                      </div>
                    ) : (
                      inv.balance_due > 0 && <p className="text-[10px] text-amber-600">${fmtMoney(inv.balance_due)}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
      </>
      )}

      {/* Create/Edit Dialog with Wizard */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setHighlightPaymentId(null); }}>
        <DialogContent className={`${editing && isViewMode ? 'max-w-5xl' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          {editing && isViewMode ? (
            <InvoiceDetailView
              invoice={editing}
              customer={contacts.find(c => c.id === editing.customer_id) || null}
              relations={detailRelations}
              highlightPaymentId={highlightPaymentId}
              canEditStatus={hasPermission('invoices', 'edit')}
              onEdit={() => setIsViewMode(false)}
              onSend={() => openSendDialog(editing)}
              onRecordPayment={(pr) => openPaymentDialog(editing, pr || null)}
              onSendReceipt={(paymentId) => openReceiptDialog(editing, paymentId || null)}
              onDispute={() => openDisputeDialog(editing)}
              onCancel={() => openCancelDialog(editing)}
              onStatusChange={handleInvoiceStatusChange}
              onChanged={() => { refreshEditingInvoice(editing.id); fetchData(); }}
              onNewPaymentRequest={() => openPaymentRequestDialog(editing)}
              onDeletePaymentRequest={(prId) => handleDeletePaymentRequest(editing.id, prId)}
            />
          ) : (
          <>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('invoices.editInvoice') : t('invoices.newInvoice')}</DialogTitle>
              {editing && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Editando</span>
              )}
            </div>
            {editing && <RelatedDocumentsBar entityType="invoice" entityId={editing.id} className="mt-2" />}
          </DialogHeader>
          
          {/* Wizard Steps Indicator - Only show for new invoices */}
          {!editing && !isViewMode && (
            <div className="flex items-center justify-center mb-6 px-2">
              {[
                { num: 1, icon: Users, label: t('invoices.wizard.step1') },
                { num: 2, icon: FileText, label: t('invoices.wizard.step2') },
              ].map((step, idx) => (
                <div key={step.num} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => step.num <= wizardStep && setWizardStep(step.num)}
                    className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${
                      wizardStep === step.num 
                        ? 'bg-[#003366] text-white' 
                        : wizardStep > step.num 
                          ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200' 
                          : 'bg-gray-100 text-gray-400'
                    }`}
                    disabled={step.num > wizardStep}
                  >
                    <div className="flex items-center gap-1.5">
                      {wizardStep > step.num ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <step.icon className="w-4 h-4" />
                      )}
                      <span className="text-xs font-semibold">{step.label}</span>
                    </div>
                  </button>
                  {idx < 1 && (
                    <ChevronRight className={`w-5 h-5 mx-2 ${wizardStep > step.num ? 'text-emerald-400' : 'text-gray-300'}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSave} className={`space-y-5 ${editing && isViewMode ? 'view-mode-form' : ''}`}>
            {/* STEP 1: Customer Selection */}
            {(!editing && wizardStep === 1) && (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <Users className="w-12 h-12 mx-auto text-[#003366] mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('invoices.wizard.selectOrCreateCustomer')}</h3>
                </div>
                
                {!showNewCustomerForm ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('common.customer')}</Label>
                      <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                        <SelectTrigger data-testid="invoice-customer-select" className="h-12">
                          <SelectValue placeholder={t('invoices.selectCustomer')} />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.filter(c => c.is_customer).map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span>{contactPrimaryLabel(c)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {form.customer_id && (
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Check className="w-5 h-5" />
                          <span className="font-medium">{getCustomerName(form.customer_id)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                      <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">o</span></div>
                    </div>
                    
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewCustomerForm(true)}
                      className="w-full h-12 border-dashed border-2"
                      data-testid="create-new-customer-invoice-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('invoices.wizard.createNewCustomer')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">{t('invoices.wizard.createNewCustomer')}</h4>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCustomerForm(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input 
                        placeholder={t('contacts.firstName') + ' *'} 
                        value={newCustomerForm.first_name}
                        onChange={e => setNewCustomerForm({...newCustomerForm, first_name: e.target.value})}
                        data-testid="new-customer-first-name-invoice"
                      />
                      <Input 
                        placeholder={t('contacts.lastName')} 
                        value={newCustomerForm.last_name}
                        onChange={e => setNewCustomerForm({...newCustomerForm, last_name: e.target.value})}
                      />
                      <Input 
                        placeholder={t('contacts.companyName')} 
                        value={newCustomerForm.company_name}
                        onChange={e => setNewCustomerForm({...newCustomerForm, company_name: e.target.value})}
                        className="col-span-2"
                      />
                      <Input 
                        placeholder="(786) 510-4512" 
                        value={newCustomerForm.phone}
                        onChange={e => setNewCustomerForm({...newCustomerForm, phone: formatPhoneNumber(e.target.value)})}
                      />
                      <Input 
                        placeholder={t('common.email')} 
                        type="email"
                        value={newCustomerForm.email}
                        onChange={e => setNewCustomerForm({...newCustomerForm, email: e.target.value})}
                      />
                    </div>
                    <Button 
                      type="button" 
                      onClick={handleCreateCustomer}
                      className="w-full bg-[#003366] hover:bg-[#004080]"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {t('invoices.wizard.createNewCustomer')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Invoice Items (or Full Edit Form) */}
            {(editing || wizardStep === 2) && (
              <div className="space-y-4">
                {!editing && (
                  <div className="text-center pb-4 border-b">
                    <FileText className="w-12 h-12 mx-auto text-[#003366] mb-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{t('invoices.wizard.invoiceItems')}</h3>
                  </div>
                )}

                {/* Payment Progress Bar - shown for existing invoices with payments */}
                {editing && editing.total > 0 && (editing.amount_paid > 0 || editing.status === 'partial') && (
                  <div className="p-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50/80 to-slate-50/80" data-testid="invoice-payment-progress-card">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Progreso de Pago</p>
                      <p className="text-sm font-bold text-emerald-700">
                        {Math.round(((editing.amount_paid || 0) / editing.total) * 100)}%
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                      <div
                        className="bg-emerald-500 h-2.5 rounded-full transition-all duration-700"
                        data-testid="invoice-payment-progress-bar"
                        style={{ width: `${Math.min(100, ((editing.amount_paid || 0) / editing.total) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-emerald-600">Pagado: <b>${fmtMoney(editing.amount_paid)}</b></span>
                      <span className="text-xs text-gray-500">de <b>${fmtMoney(editing.total)}</b></span>
                      {editing.balance_due > 0 && (
                        <span className="text-xs text-amber-600">Pendiente: <b>${fmtMoney(editing.balance_due)}</b></span>
                      )}
                    </div>
                    {/* Payment history */}
                    {editing.payments?.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-emerald-100 space-y-1.5" data-testid="invoice-payment-history">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Historial de Pagos</p>
                        {editing.payments.map((p, i) => {
                          const isHighlighted = !!highlightPaymentId && p.id === highlightPaymentId;
                          const recorded = p.date?.slice(0, 10);
                          const received = p.received_date?.slice(0, 10) || recorded;
                          const sameDate = received === recorded;
                          const methodClass = PAYMENT_METHOD_COLORS[p.method] || 'bg-gray-50 border-gray-200 text-gray-600';
                          return (
                            <div
                              key={p.id || i}
                              ref={(el) => { if (isHighlighted && el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                              data-testid={`invoice-payment-row-${p.id || i}`}
                              className={`rounded-md border px-2 py-1.5 transition-colors ${methodClass} ${isHighlighted ? 'ring-2 ring-amber-400' : ''}`}
                            >
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold uppercase tracking-wide">{PAYMENT_METHOD_LABELS[p.method] || p.method}</span>
                                <span className="font-bold">${fmtMoney(p.amount)}</span>
                              </div>
                              <div className="flex items-center justify-between text-[10px] mt-0.5 opacity-80">
                                <span data-testid={`invoice-payment-dates-${p.id || i}`}>
                                  {sameDate ? `Received ${received}` : `Received ${received} · Recorded ${recorded}`}
                                </span>
                                {p.surcharge_amount > 0 && (
                                  <span data-testid={`invoice-payment-surcharge-${p.id || i}`}>
                                    +${fmtMoney(p.surcharge_amount)} surcharge (${fmtMoney(p.total_charged)} charged)
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Cancellation/Dispute info banner */}
                {editing && editing.status === 'cancelled' && editing.cancellation_reason && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200" data-testid="invoice-cancelled-banner">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-red-700">Invoice Cancelled</p>
                        <p className="text-xs text-red-600 mt-0.5">{editing.cancellation_reason}</p>
                        {editing.cancelled_at && <p className="text-[10px] text-red-400 mt-1">{editing.cancelled_at.slice(0, 10)}</p>}
                      </div>
                    </div>
                  </div>
                )}
                {editing && editing.status === 'in_dispute' && editing.dispute_reason && (
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid="invoice-dispute-banner">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-rose-700">In Dispute</p>
                        <p className="text-xs text-rose-600 mt-0.5">{editing.dispute_reason}</p>
                        {editing.disputed_at && <p className="text-[10px] text-rose-400 mt-1">{editing.disputed_at.slice(0, 10)}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* PAYMENT REQUESTS SECTION — Prompt Z: movida al nuevo
                    InvoiceDetailView (view mode). Acá no se renderiza más. */}

                {/* Summary for wizard mode */}
                {!editing && (
                  <div className="p-3 bg-gray-50 rounded-lg mb-4">
                    <p className="text-xs text-gray-500">{t('common.customer')}</p>
                    <p className="font-medium text-sm">{getCustomerName(form.customer_id) || '-'}</p>
                  </div>
                )}

                {/* Edit mode: show customer select */}
                {editing && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('common.customer')}</Label>
                      <Select value={form.customer_id} onValueChange={v => setForm({ ...form, customer_id: v })}>
                        <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder={t('invoices.selectCustomer')} /></SelectTrigger>
                        <SelectContent>
                          {contacts.filter(c => c.is_customer).map(c => (
                            <SelectItem key={c.id} value={c.id}>{contactPrimaryLabel(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('common.type')}</Label>
                      <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                        <SelectTrigger data-testid="invoice-payment-method"><SelectValue placeholder={t('invoices.paymentMethod')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('common.none')}</SelectItem>
                          <SelectItem value="credit_card">{t('invoices.creditCard')} (+{cardSurchargeRatePct}%)</SelectItem>
                          <SelectItem value="ach">ACH (0%)</SelectItem>
                          <SelectItem value="cash">{t('invoices.cash')}</SelectItem>
                          <SelectItem value="check">{t('invoices.check')}</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.payment_method === 'credit_card' && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> +{cardSurchargeRatePct}%
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Dual Address Display: Mailing Address + Job Site Address.
                    Register Owner lives INSIDE the Job Site card now, and
                    Parcel # shows only once here (Alian: "menos es mejor",
                    used to repeat the parcel twice + a separate owner
                    header). */}
                {editing && (editing.well_address || editing.mailing_address?.address) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="invoice-address-cards">
                    {/* MAILING ADDRESS / BILL TO */}
                    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Bill To / Mailing Address</p>
                      {editing.mailing_address?.address ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">{editing.mailing_address.address}</p>
                          {(editing.mailing_address.city || editing.mailing_address.state || editing.mailing_address.zip) && (
                            <p className="text-xs text-gray-600">
                              {[editing.mailing_address.city, editing.mailing_address.state].filter(Boolean).join(', ')} {editing.mailing_address.zip || ''}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No mailing address</p>
                      )}
                    </div>
                    {/* JOB SITE ADDRESS — Register Owner + Parcel live here, once */}
                    <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/50" data-testid="invoice-job-site-card">
                      <p className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold mb-1">Job Site Address</p>
                      {editing.well_address ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">{editing.well_address}</p>
                          {(editing.well_city || editing.well_state || editing.well_zip) && (
                            <p className="text-xs text-gray-600">
                              {[editing.well_city, editing.well_state].filter(Boolean).join(', ')} {editing.well_zip || ''}
                            </p>
                          )}
                          {editing.property_info?.county && <p className="text-xs text-gray-600">{editing.property_info.county} County</p>}
                          {editing.property_info?.owner_name && (
                            <p className="text-xs text-gray-600 mt-1"><span className="font-semibold text-blue-600">Register Owner:</span> {editing.property_info.owner_name}</p>
                          )}
                          {editing.property_info?.parcel_id && <p className="text-xs text-gray-600 font-mono">Parcel: {editing.property_info.parcel_id}</p>}
                        </>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No job site address</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Method for new invoices */}
                {!editing && (
                  <div className="space-y-2">
                    <Label>{t('invoices.paymentMethod')}</Label>
                    <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                      <SelectTrigger><SelectValue placeholder={t('invoices.paymentMethod')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('common.none')}</SelectItem>
                        <SelectItem value="credit_card">{t('invoices.creditCard')} (+{cardSurchargeRatePct}%)</SelectItem>
                        <SelectItem value="ach">ACH (0%)</SelectItem>
                        <SelectItem value="cash">{t('invoices.cash')}</SelectItem>
                        <SelectItem value="check">{t('invoices.check')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.payment_method === 'credit_card' && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <CreditCard className="w-3 h-3" /> +{cardSurchargeRatePct}% {t('invoices.surchargeNote')}
                      </p>
                    )}
                  </div>
                )}

                {/* Line Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-gray-700">{t('invoices.items')}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-invoice-item-btn">
                      <Plus className="w-3 h-3 mr-1" /> {t('invoices.addItem')}
                    </Button>
                  </div>
                  
                  {/* Info about SKU search */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50/50 border border-blue-100">
                    <Tag className="w-4 h-4 text-blue-500" />
                    <p className="text-xs text-blue-600">Busca por SKU o nombre para agregar items del catálogo, o escribe manualmente.</p>
                  </div>
                  
                  <div className="space-y-3">
                    {form.items.map((item, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-gray-50 border border-gray-100 space-y-2" data-testid={`invoice-item-row-${idx}`}>
                        {/* Top row: SKU search + Name */}
                        <div className="flex gap-2 items-start relative">
                          <div className="relative w-32">
                            <Input
                              data-testid={`invoice-item-sku-${idx}`}
                              value={item.sku || ''}
                              onChange={e => {
                                updateItem(idx, 'sku', e.target.value);
                                searchLineItems(e.target.value, idx);
                              }}
                              onFocus={() => {
                                setActiveItemIdx(idx);
                                if (item.sku) searchLineItems(item.sku, idx);
                              }}
                              placeholder="SKU..."
                              className="bg-white h-9 font-mono text-sm"
                            />
                            {/* SKU Search Results Dropdown */}
                            {activeItemIdx === idx && lineItemResults.length > 0 && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-auto w-80">
                                {lineItemResults.map((li) => (
                                  <button
                                    key={li.id}
                                    type="button"
                                    onClick={() => selectLineItem(li, idx)}
                                    className="w-full px-3 py-2.5 text-left hover:bg-blue-50 border-b last:border-0 transition-colors"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm text-[#003366] font-bold">{li.sku}</span>
                                        {li.supplier_sku && <span className="text-[10px] text-gray-400 font-mono">({li.supplier_sku})</span>}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-emerald-600">${fmtMoney(li.unit_price)}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{li.source === 'inventory' ? 'Stock' : 'Item'}</span>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-800 truncate mt-0.5">{li.name}</p>
                                    {li.description && li.description !== li.name && (
                                      <p className="text-xs text-gray-400 truncate">{li.description}</p>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <Input
                            data-testid={`invoice-item-name-${idx}`}
                            value={item.name || ''}
                            onChange={e => updateItem(idx, 'name', e.target.value)}
                            placeholder="Nombre del item"
                            className="flex-1 bg-white h-9 font-medium"
                          />
                          {form.items.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Second row: Description (auto-expanding) */}
                        <AutoGrowTextarea
                          data-testid={`invoice-item-desc-${idx}`}
                          value={item.description}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder={t('common.description')}
                          className="bg-white text-sm"
                        />
                        
                        {/* Third row: Quantity, Unit, Price, Subtotal */}
                        <div className="flex gap-2 items-center">
                          <div className="flex items-center gap-1">
                            <Input
                              data-testid={`invoice-item-qty-${idx}`}
                              type="number"
                              value={item.quantity || ''}
                              onChange={e => updateItem(idx, 'quantity', e.target.value)}
                              onFocus={e => e.target.select()}
                              className="w-20 bg-white h-9"
                              min="0"
                            />
                            <Input
                              data-testid={`invoice-item-unit-${idx}`}
                              value={item.unit || 'unit'}
                              onChange={e => updateItem(idx, 'unit', e.target.value)}
                              placeholder="unit"
                              className="w-16 bg-white h-9 text-xs"
                            />
                          </div>
                          <span className="text-gray-400">x</span>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500 text-sm">$</span>
                            <Input
                              data-testid={`invoice-item-price-${idx}`}
                              type="number"
                              value={item.unit_price || ''}
                              onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                              onFocus={e => e.target.select()}
                              placeholder="Precio"
                              className="w-24 bg-white h-9"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <span className="text-gray-400">=</span>
                          <span className="text-sm font-semibold text-gray-700 min-w-[80px] text-right">
                            ${fmtMoney((item.quantity || 0) * (item.unit_price || 0))}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end px-3 space-y-1">
                    <div className="text-right space-y-1.5">
                      <div><span className="text-sm text-gray-500">{t('common.subtotal')}: </span><span className="text-sm font-semibold">${fmtMoney(calcSubtotal())}</span></div>
                      
                      {/* Discount Section */}
                      <div data-testid="invoice-discount-section" className="flex items-center gap-2 py-2 px-3 rounded-lg bg-amber-50/80 border border-amber-200/60">
                        <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v, discount_value: v === 'none' ? 0 : form.discount_value})}>
                          <SelectTrigger data-testid="invoice-discount-type" className="h-8 w-[130px] text-xs bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Discount</SelectItem>
                            <SelectItem value="percentage">% Percentage</SelectItem>
                            <SelectItem value="fixed">$ Discount</SelectItem>
                          </SelectContent>
                        </Select>
                        {form.discount_type !== 'none' && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-amber-700">{form.discount_type === 'percentage' ? '%' : '$'}</span>
                            <Input
                              data-testid="invoice-discount-value"
                              type="number"
                              value={form.discount_value}
                              onChange={e => setForm({...form, discount_value: parseFloat(e.target.value) || 0})}
                              className="w-20 h-8 text-sm bg-white"
                              min="0"
                            />
                          </div>
                        )}
                        {calcDiscountAmount() > 0 && (
                          <span className="text-xs font-medium text-red-600 ml-auto">Discount: -${fmtMoney(calcDiscountAmount())}</span>
                        )}
                      </div>
                      
                      {form.payment_method === 'credit_card' && (
                        <div><span className="text-sm text-amber-600">+{cardSurchargeRatePct}%: </span><span className="text-sm font-semibold text-amber-600">${fmtMoney(calcSurcharge())}</span></div>
                      )}
                      <div className="pt-1 border-t border-gray-200"><span className="text-sm font-semibold text-gray-700">Total: </span><span data-testid="invoice-total" className="text-lg font-bold text-gray-900">${fmtMoney(calcTotal())}</span></div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('common.notes')}</Label>
                  <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder={t('common.notes') + '...'} rows={2} />
                </div>
              </div>
            )}

            {/* Documents (auto-archived copies of sent invoice PDFs) */}
            {editing && <DocumentsSection entityType="invoice" entityId={editing.id} />}

            {/* Activity Log */}
            {editing && <ActivityLog entityType="invoices" entityId={editing?.id} />}

            {/* Navigation Buttons */}
            {!(editing && isViewMode) && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              {!editing && wizardStep > 1 && (
                <Button type="button" variant="outline" onClick={handlePrevStep} className="sm:mr-auto">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('invoices.wizard.prevStep')}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => { if (!isViewMode && editing) { setIsViewMode(true); } else { setDialogOpen(false); } }}>{!isViewMode && editing ? t('common.cancel') : t('common.close')}</Button>
              {!editing && wizardStep < 2 ? (
                <Button type="button" onClick={handleNextStep} className="bg-[#003366] hover:bg-[#004080] text-white">
                  {t('invoices.wizard.nextStep')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button data-testid="save-invoice-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                  {editing ? t('invoices.saveChanges') : t('invoices.wizard.finishInvoice')}
                </Button>
              )}
            </DialogFooter>
            )}
          </form>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send / Resend (Email + SMS) */}
      <SendDocumentDialog
        open={!!sendDialogInv}
        onClose={() => setSendDialogInv(null)}
        docType="invoice"
        doc={sendDialogInv}
        customer={contacts.find(c => c.id === sendDialogInv?.customer_id)}
        onSent={handleSendDialogSent}
      />

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" /> {t('invoices.collectPayment')}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                <p className="text-sm font-medium">{selectedInvoice.invoice_number}</p>
                <p className="text-xs text-gray-500">Balance: <span className="font-semibold text-amber-600">${fmtMoney(selectedInvoice.balance_due)}</span></p>
                {paymentForm.payment_request_id && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1" data-testid="payment-linked-request-note">
                    <CreditCard className="w-3 h-3" /> Settling the requested amount — edit below if the customer paid a different amount.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('common.amount')} *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">$</span>
                  <Input
                    data-testid="payment-amount"
                    type="text"
                    inputMode="decimal"
                    className="pl-7"
                    value={paymentForm.amountStr !== undefined ? paymentForm.amountStr : (paymentForm.amount > 0 ? fmtMoney(paymentForm.amount) : '')}
                    onChange={e => {
                      // Accept typed commas (thousand separators) and ignore them when parsing —
                      // Alian: "intenté ponerla pero no sale" (TEMA 2.3).
                      const raw = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '');
                      const dots = raw.split('.');
                      const cleaned = dots.length > 2 ? dots[0] + '.' + dots.slice(1).join('') : raw;
                      setPaymentForm({ ...paymentForm, amountStr: e.target.value.replace(/[^0-9.,]/g, ''), amount: parseFloat(cleaned) || 0 });
                    }}
                    onFocus={e => e.target.select()}
                    onBlur={() => setPaymentForm(f => ({ ...f, amountStr: f.amount > 0 ? fmtMoney(f.amount) : '' }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('common.type')} *</Label>
                <Select value={paymentForm.method} onValueChange={v => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="ach">ACH / Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* TEMA 2.1: live breakdown — the customer pays the surcharge, the
                  invoice balance only drops by the amount owed. */}
              {paymentForm.method === 'credit_card' && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1" data-testid="payment-surcharge-breakdown">
                  <div className="flex items-center justify-between text-xs text-gray-700">
                    <span>Amount applied to invoice:</span>
                    <span className="font-semibold">${fmtMoney(paymentForm.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-amber-700">
                    <span>Card surcharge ({cardSurchargeRatePct}%):</span>
                    <span className="font-semibold" data-testid="payment-surcharge-amount">${fmtMoney(paymentForm.amount * cardSurchargeRatePct / 100)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-1 border-t border-amber-200 text-amber-900">
                    <span className="font-medium">Total charged to customer:</span>
                    <span className="font-bold" data-testid="payment-total-charged">${fmtMoney(paymentForm.amount * (1 + cardSurchargeRatePct / 100))}</span>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Date received</Label>
                <Input
                  data-testid="payment-received-date"
                  type="date"
                  value={paymentForm.received_date || ''}
                  onChange={e => setPaymentForm({ ...paymentForm, received_date: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                />
                <p className="text-[11px] text-gray-400">When the money actually landed — e.g. wires that post a few days before you load them here.</p>
              </div>
              <div className="space-y-2">
                <Label>Reference / Transaction #</Label>
                <Input
                  data-testid="payment-reference-input"
                  value={paymentForm.reference}
                  onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="e.g. Check #1042, ACH trace, confirmation code"
                />
                <p className="text-[11px] text-gray-400">Check number, ACH trace number or confirmation code.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button data-testid="confirm-payment-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {t('common.confirm')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Fix K (2026-09-02): Send Receipt — solo aparece si hay >=1 pago. */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-md" data-testid="send-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow']">Send Receipt</DialogTitle>
          </DialogHeader>
          {receiptInvoice && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Send a payment receipt for invoice <b>{receiptInvoice.invoice_number}</b> to the customer.
              </p>
              <RadioGroup value={receiptSelection} onValueChange={setReceiptSelection} className="space-y-2">
                <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50" data-testid="receipt-option-last">
                  <RadioGroupItem value="last" />
                  <span className="text-sm">Last payment only{(() => { const p = (receiptInvoice.payments || [])[receiptInvoice.payments.length - 1]; return p ? ` — $${fmtMoney(p.amount)} (${PAYMENT_METHOD_LABELS[p.method] || p.method})` : ''; })()}</span>
                </label>
                {(receiptInvoice.payments || []).length > 1 && (
                  <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50" data-testid="receipt-option-all">
                    <RadioGroupItem value="all" />
                    <span className="text-sm">Consolidated receipt — all {receiptInvoice.payments.length} payments</span>
                  </label>
                )}
                {(receiptInvoice.payments || []).map((p) => (
                  <label key={p.id} className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50" data-testid={`receipt-option-${p.id}`}>
                    <RadioGroupItem value={p.id} />
                    <span className="text-sm">${fmtMoney(p.amount)} ({PAYMENT_METHOD_LABELS[p.method] || p.method}) — {(p.received_date || p.date || '').slice(0, 10)}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReceiptDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button data-testid="confirm-send-receipt-btn" disabled={sendingReceipt} onClick={handleSendReceipt} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> {sendingReceipt ? 'Sending...' : 'Send Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Request Creation Dialog */}
      <Dialog open={prDialogOpen} onOpenChange={setPrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" /> Create Payment Request
            </DialogTitle>
          </DialogHeader>
          {prInvoice && (
            <form onSubmit={handleCreatePaymentRequest} className="space-y-4">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">{prInvoice.invoice_number}</p>
                    <p className="text-xs text-gray-500">{prInvoice.title}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">${fmtMoney(prInvoice.total)}</p>
                    <p className="text-xs text-gray-500">
                      Requested: ${fmtMoney((prInvoice.payment_requests || []).filter(pr => pr.status !== 'cancelled').reduce((s, pr) => s + (pr.amount || 0), 0))}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={prForm.request_type} onValueChange={v => {
                  // Fix D (2026-08-31): Alian pidio que en una inspeccion no se
                  // hable de deposito — "esto es una inspeccion y no hace falta un
                  // deposito". Se agrega el tipo full_payment con la etiqueta
                  // "Total Amount Due". Los tres tipos existentes NO se renombran:
                  // hay solicitudes de pago guardadas con esos valores.
                  const labels = { down_payment: 'Down Payment', progress_payment: 'Progress Payment', final_payment: 'Final Payment', full_payment: 'Total Amount Due' };
                  setPrForm({ ...prForm, request_type: v, label: prForm.label || labels[v] || '' });
                }}>
                  <SelectTrigger data-testid="pr-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="down_payment">Down Payment (Initial)</SelectItem>
                    <SelectItem value="progress_payment">Progress Payment</SelectItem>
                    <SelectItem value="final_payment">Final Payment</SelectItem>
                    <SelectItem value="full_payment">Total Amount Due</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Label (optional)</Label>
                <Input data-testid="pr-label" value={prForm.label} onChange={e => setPrForm({ ...prForm, label: e.target.value })} placeholder="e.g. Initial Payment, Second Payment" />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => setPrForm({...prForm, amount_mode: 'fixed', percentage: 0})}
                    className={`flex-1 h-8 rounded-md text-xs font-medium border transition-all ${prForm.amount_mode !== 'percentage' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    data-testid="pr-mode-fixed">$ Fixed</button>
                  <button type="button" onClick={() => setPrForm({...prForm, amount_mode: 'percentage', percentage: 40})}
                    className={`flex-1 h-8 rounded-md text-xs font-medium border transition-all ${prForm.amount_mode === 'percentage' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    data-testid="pr-mode-percentage">% Percentage</button>
                </div>
                {prForm.amount_mode === 'percentage' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <button type="button" data-testid="pr-base-total"
                        onClick={() => {
                          const total = prInvoice?.total || 0;
                          setPrForm(f => ({ ...f, percentage_base: 'total', amount: Math.round(total * (f.percentage || 0) / 100 * 100) / 100 }));
                        }}
                        className={`flex-1 h-7 rounded-md text-[11px] font-medium border transition-all ${prForm.percentage_base !== 'remaining' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-300'}`}>
                        % of Total
                      </button>
                      <button type="button" data-testid="pr-base-remaining"
                        onClick={() => {
                          const requested = (prInvoice?.payment_requests || []).filter(p => p.status !== 'cancelled').reduce((s, p) => s + (p.amount || 0), 0);
                          const remaining = Math.max(0, (prInvoice?.total || 0) - requested);
                          setPrForm(f => ({ ...f, percentage_base: 'remaining', amount: Math.round(remaining * (f.percentage || 0) / 100 * 100) / 100 }));
                        }}
                        className={`flex-1 h-7 rounded-md text-[11px] font-medium border transition-all ${prForm.percentage_base === 'remaining' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-300'}`}>
                        % of Remaining Balance
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input data-testid="pr-percentage" type="number" onWheel={e => e.target.blur()} value={prForm.percentage} onChange={e => {
                        const pct = parseFloat(e.target.value) || 0;
                        const total = prInvoice?.total || 0;
                        const requested = (prInvoice?.payment_requests || []).filter(pr => pr.status !== 'cancelled').reduce((s, pr) => s + (pr.amount || 0), 0);
                        const remaining = Math.max(0, total - requested);
                        const base = prForm.percentage_base === 'remaining' ? remaining : total;
                        setPrForm({...prForm, percentage: pct, amount: Math.round(base * pct / 100 * 100) / 100});
                      }} step="1" min="0" max="100" className="flex-1" />
                      <span className="text-sm font-medium text-gray-500">%</span>
                    </div>
                    {(() => {
                      const total = prInvoice?.total || 0;
                      const requested = (prInvoice?.payment_requests || []).filter(pr => pr.status !== 'cancelled').reduce((s, pr) => s + (pr.amount || 0), 0);
                      const remaining = Math.max(0, total - requested);
                      const base = prForm.percentage_base === 'remaining' ? remaining : total;
                      return (
                        <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded" data-testid="pr-percentage-preview">
                          {prForm.percentage}% of {prForm.percentage_base === 'remaining' ? 'remaining balance' : 'total'} (${fmtMoney(base)}) = <b>${fmtMoney(prForm.amount)}</b>
                        </p>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">$</span>
                    <Input
                      data-testid="pr-amount"
                      type="text"
                      inputMode="decimal"
                      value={prForm.amountStr !== undefined ? prForm.amountStr : (prForm.amount > 0 ? prForm.amount.toFixed(2) : '')}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                        const dots = raw.split('.');
                        const cleaned = dots.length > 2 ? dots[0] + '.' + dots.slice(1).join('') : raw;
                        setPrForm({ ...prForm, amountStr: cleaned, amount: parseFloat(cleaned) || 0 });
                      }}
                      onFocus={e => e.target.select()}
                      onBlur={() => {
                        const val = parseFloat(prForm.amountStr) || 0;
                        setPrForm({ ...prForm, amountStr: val > 0 ? val.toFixed(2) : '', amount: val });
                      }}
                      className="pl-8"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={prForm.notes} onChange={e => setPrForm({ ...prForm, notes: e.target.value })} placeholder="Optional notes..." />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPrDialogOpen(false)}>Cancel</Button>
                <Button data-testid="confirm-pr-btn" type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Create Request</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Job Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-violet-500" /> Convert to Job
            </DialogTitle>
          </DialogHeader>
          {convertInvoice && (
            <div className="space-y-5">
              {/* Invoice Info Header */}
              <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-violet-900">{convertInvoice.invoice_number}</p>
                    <p className="text-xs text-violet-600">{convertInvoice.title}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-violet-900">${fmtMoney(convertInvoice.total)}</p>
                    <p className="text-xs text-violet-500">{getCustomerName(convertInvoice.customer_id)}</p>
                  </div>
                </div>
              </div>

              {/* Line Items Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" /> Select Items for this Job
                </Label>
                <div className="space-y-2">
                  {(convertInvoice.items || []).map((item, idx) => {
                    const converted = isItemConverted(convertInvoice, idx);
                    const jobInfo = getConvertedJobInfo(convertInvoice, idx);
                    const isSelected = selectedItemIndices.includes(idx);
                    
                    return (
                      <div 
                        key={idx}
                        data-testid={`convert-item-${idx}`}
                        onClick={() => !converted && toggleItemSelection(idx)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                          converted 
                            ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                            : isSelected
                              ? 'bg-violet-50 border-violet-400 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          data-testid={`convert-checkbox-${idx}`}
                          checked={isSelected}
                          disabled={!!converted}
                          onCheckedChange={() => !converted && toggleItemSelection(idx)}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${converted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {item.description || item.name || `Item ${idx + 1}`}
                          </p>
                          {item.sku && <p className="text-xs text-gray-400">SKU: {item.sku}</p>}
                          {converted && jobInfo && (
                            <button
                              type="button"
                              data-testid={`converted-job-link-${idx}`}
                              onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${jobInfo.job_id}`); }}
                              className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1 mt-0.5 font-medium"
                            >
                              <Check className="w-3 h-3" /> {jobInfo.job_number}
                              {jobInfo.job_type && <span className="text-emerald-600">· {jobCategoryLabel(jobInfo.job_type)}</span>}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-500">
                            {item.quantity} x ${fmtMoney(item.unit_price)}
                          </p>
                          <p className={`text-sm font-semibold ${converted ? 'text-gray-400' : 'text-gray-700'}`}>
                            ${fmtMoney((item.quantity || 1) * (item.unit_price || 0))}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected Total */}
                {selectedItemIndices.length > 0 && (
                  <div className="flex justify-end p-2 bg-violet-50 rounded-lg">
                    <div className="text-right">
                      <p className="text-xs text-violet-600">{selectedItemIndices.length} item(s) selected</p>
                      <p className="text-base font-bold text-violet-900">
                        ${fmtMoney(selectedItemIndices.reduce((sum, idx) => {
                          const item = convertInvoice.items[idx];
                          return sum + ((item?.quantity || 1) * (item?.unit_price || 0));
                        }, 0))}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Job Configuration */}
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <UserCheck className="w-4 h-4" /> Assign to
                  </Label>
                  {/* Fix D (2026-08-31): al elegir la persona se precarga SU categoria
                      por defecto (users.default_job_category), misma logica que el
                      dialogo de conversion del proyecto. Sigue editable; si el
                      usuario ya la toco a mano, no se pisa. */}
                  <EmployeeCombobox
                    users={systemUsers}
                    value={convertAssignedTo}
                    onChange={(userId) => {
                      setConvertAssignedTo(userId);
                      const u = systemUsers.find(x => x.id === userId);
                      if (u?.default_job_category && !convertCategoryTouched) {
                        setConvertJobCategory(u.default_job_category);
                      }
                    }}
                    testId="convert-assigned-to"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Job Category</Label>
                    <Select value={convertJobCategory} onValueChange={(v) => { setConvertCategoryTouched(true); setConvertJobCategory(v); }}>
                      <SelectTrigger data-testid="convert-job-category" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {JOB_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Job Title (optional)</Label>
                    <Input
                      data-testid="convert-job-title"
                      value={convertJobTitle}
                      onChange={e => setConvertJobTitle(e.target.value)}
                      placeholder="Auto-generated if empty"
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Progress indicator for multi-job creation */}
              {convertInvoice.converted_items && Object.keys(convertInvoice.converted_items).length > 0 && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-emerald-700">Conversion Progress</p>
                    <p className="text-xs text-emerald-600">
                      {Object.keys(convertInvoice.converted_items).length} / {(convertInvoice.items || []).length} items assigned
                    </p>
                  </div>
                  <div className="w-full bg-emerald-200 rounded-full h-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(Object.keys(convertInvoice.converted_items).length / (convertInvoice.items || []).length) * 100}%` }}
                    />
                  </div>
                  <div className="mt-2 space-y-1">
                    {Object.entries(convertInvoice.converted_items).map(([itemIdx, info]) => (
                      <button
                        key={itemIdx}
                        type="button"
                        data-testid={`conversion-progress-link-${itemIdx}`}
                        onClick={() => navigate(`/jobs/${info.job_id}`)}
                        className="block w-full text-left text-[11px] text-emerald-700 hover:underline"
                      >
                        Item {parseInt(itemIdx) + 1}: {(convertInvoice.items[parseInt(itemIdx)]?.description || '').substring(0, 30)}... → <strong>{info.job_number}</strong>
                        {info.job_type ? ` · ${jobCategoryLabel(info.job_type)}` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setConvertDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button 
                  data-testid="confirm-convert-to-job"
                  type="button" 
                  onClick={handleConvertToJob}
                  disabled={convertLoading || selectedItemIndices.length === 0 || !convertAssignedTo}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {convertLoading ? (
                    <><span className="animate-spin mr-2">...</span> Creating...</>
                  ) : (
                    <><Briefcase className="w-4 h-4 mr-2" /> Create Job ({selectedItemIndices.length} items)</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" /> Cancel Invoice
            </DialogTitle>
          </DialogHeader>
          {cancelInvoice && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm font-semibold text-red-900">{cancelInvoice.invoice_number}</p>
                <p className="text-xs text-red-600">{cancelInvoice.title}</p>
                <p className="text-xs text-red-500 mt-1">Total: ${fmtMoney(cancelInvoice.total)}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-700">The invoice will not be deleted, it will only change to "Cancelled" status. The record is kept for your files.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('invoices.cancellationReason')}</Label>
                <Textarea
                  data-testid="cancel-reason-input"
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="e.g.: Customer did not want to continue, Billing error..."
                  rows={3}
                  className="resize-none"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)}>{t('common.close')}</Button>
                <Button
                  data-testid="confirm-cancel-invoice-btn"
                  type="button"
                  onClick={handleCancelInvoice}
                  disabled={!cancelReason.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <XCircle className="w-4 h-4 mr-2" /> Confirm Cancellation
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" /> Delete Invoice
            </DialogTitle>
          </DialogHeader>
          {deleteInvoice && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm font-semibold text-red-900">{deleteInvoice.invoice_number}</p>
                <p className="text-xs text-red-600">{deleteInvoice.title}</p>
                <p className="text-xs text-red-500 mt-1">Total: ${fmtMoney(deleteInvoice.total)}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-700">This will permanently delete the invoice. This action cannot be undone.</p>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.close')}</Button>
                <Button
                  data-testid="confirm-delete-invoice-btn"
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-700 hover:bg-red-800 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {deleting ? 'Deleting...' : 'Delete Permanently'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispute Invoice Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500" /> Mark in Dispute
            </DialogTitle>
          </DialogHeader>
          {disputeInvoice && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                <p className="text-sm font-semibold text-rose-900">{disputeInvoice.invoice_number}</p>
                <p className="text-xs text-rose-600">{disputeInvoice.title}</p>
                <p className="text-xs text-rose-500 mt-1">Total: ${fmtMoney(disputeInvoice.total)} | Balance: ${fmtMoney(disputeInvoice.balance_due)}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-700">An invoice in dispute indicates the customer disagrees with the charge. You can still record partial payments while it is resolved.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('invoices.disputeReason')}</Label>
                <Textarea
                  data-testid="dispute-reason-input"
                  value={disputeReason}
                  onChange={e => setDisputeReason(e.target.value)}
                  placeholder="e.g.: The well does not produce the expected amount of water..."
                  rows={3}
                  className="resize-none"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDisputeDialogOpen(false)}>{t('common.close')}</Button>
                <Button
                  data-testid="confirm-dispute-invoice-btn"
                  type="button"
                  onClick={handleDisputeInvoice}
                  disabled={!disputeReason.trim()}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" /> Confirm Dispute
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Forms Attachment Dialog */}
      {customFormsInvoice && (
        <AttachCustomForm
          open={customFormsOpen}
          onClose={() => { setCustomFormsOpen(false); setCustomFormsInvoice(null); }}
          linkedType="invoice"
          linkedId={customFormsInvoice.id}
          context={{
            invoice_id: customFormsInvoice.id,
            customer_id: customFormsInvoice.customer_id,
            project_id: customFormsInvoice.project_id,
            job_id: customFormsInvoice.job_id,
          }}
          categoryFilter={null}
          title={`Custom Forms — ${customFormsInvoice.invoice_number || ''}`}
        />
      )}
    </div>
  );
}
